import { useMemo } from "react";
import { Calendar as CalIcon } from "lucide-react";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { RESTAURANT_BY_ID } from "../../constants";
import { isMain } from "../../lib/permissions";

// Stable option arrays (lifted out of JSX so they're not re-created per render).
const STATUS_OPTIONS = [
  { v: "submitted", l: "Submitted" },
  { v: "approved", l: "Approved" },
  { v: "awaiting_delivery", l: "Awaiting delivery" },
  { v: "fulfilled", l: "Fulfilled" },
  { v: "partially_fulfilled", l: "Partially fulfilled" },
  { v: "missed", l: "Missed" },
  { v: "rejected", l: "Rejected" },
  { v: "completed", l: "Completed" },
  { v: "draft", l: "Draft" },
  { v: "all", l: "All" },
];

const RESTAURANT_OPTIONS = [
  { v: "all", l: "All" },
  { v: "restaurant_1", l: RESTAURANT_BY_ID.restaurant_1.name },
  { v: "restaurant_2", l: RESTAURANT_BY_ID.restaurant_2.name },
];

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] tracking-eyebrow uppercase text-[#0B1F3A]/55">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="mt-1 h-9 bg-white border-[#E6E0D4] text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v}>
              {o.l}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function RequestFilters({ user, scope, state, setters, products, suppliers }) {
  const { status, restaurant, vendor, productId, requester, from, to } = state;
  const {
    setStatus,
    setRestaurant,
    setVendor,
    setProductId,
    setRequester,
    setFrom,
    setTo,
  } = setters;

  // Memoize option arrays so they aren't rebuilt on every keystroke.
  const vendorOptions = useMemo(
    () => [{ v: "all", l: "All" }, ...suppliers.map((s) => ({ v: s.id, l: s.name }))],
    [suppliers]
  );
  const productOptions = useMemo(
    () => [{ v: "all", l: "All" }, ...products.map((p) => ({ v: p.id, l: p.name }))],
    [products]
  );

  return (
    <div className="bg-white border border-[#E6E0D4] rounded-xl p-3 md:p-4 mb-5">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} testId="filter-status" />
        {isMain(user) && (
          <FilterSelect
            label="Restaurant"
            value={restaurant}
            onChange={setRestaurant}
            options={RESTAURANT_OPTIONS}
            testId="filter-restaurant"
          />
        )}
        {scope !== "transfer" && (
          <FilterSelect
            label="Vendor"
            value={vendor}
            onChange={setVendor}
            options={vendorOptions}
            testId="filter-vendor"
          />
        )}
        <FilterSelect
          label="Product"
          value={productId}
          onChange={setProductId}
          options={productOptions}
          testId="filter-product"
        />
        <div>
          <label className="text-[10px] tracking-eyebrow uppercase text-[#0B1F3A]/55">Requester</label>
          <Input
            data-testid="filter-requester"
            value={requester}
            onChange={(e) => setRequester(e.target.value)}
            placeholder="Name…"
            className="mt-1 h-9 bg-white border-[#E6E0D4] text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-eyebrow uppercase text-[#0B1F3A]/55">From</label>
          <div className="relative mt-1">
            <CalIcon size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#0B1F3A]/40 pointer-events-none" />
            <Input
              data-testid="filter-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 pl-7 bg-white border-[#E6E0D4] text-sm"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] tracking-eyebrow uppercase text-[#0B1F3A]/55">To</label>
          <div className="relative mt-1">
            <CalIcon size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#0B1F3A]/40 pointer-events-none" />
            <Input
              data-testid="filter-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 pl-7 bg-white border-[#E6E0D4] text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
